import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import webservice from '../app/webservice/webservice';
import {
  Table,
  Button,
  PopupWindow,
  MessagePopupCore,
} from '@utahdts/dts-react-common';
import columns from './columns/ListingsColumns';
import Listing from './modals/Listing';
import appStore from '../app/mobx/appStore';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import { usePrevious } from '../misc/hooks/usePrevious';
import { sortByLastUpdatedDesc } from '../misc/Shared';

const orgKey = (value) => String(value ?? '').trim();

const addCompanyNames = (drugs = [], companies = []) => {
  const companyByOrg = new Map();
  companies.forEach((company) => {
    const id = orgKey(company?.id);
    const fein = orgKey(company?.fein);
    if (id) {
      companyByOrg.set(id, company);
    }
    if (fein) {
      companyByOrg.set(fein, company);
    }
  });

  return drugs.map((drug) => {
    if (orgKey(drug?.companyName)) {
      return drug;
    }
    const company = companyByOrg.get(orgKey(drug?.org));
    return {
      ...drug,
      companyName: company?.name || '',
    };
  });
};

const Listings = () => {
  const [isBusy, setBusy] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [showDrug, displayDrug] = useState(false);
  const [drug, setDrug] = useState({});
  const [isNew, setIsNew] = useState(true);
  const [showDelete, displayDelete] = useState(false);
  const [error, setError] = useState(undefined);
  const prevOrg = usePrevious(appStore.currentOrg);

  const displayError = (err) => {
    if (err) {
      console.error('[Listings] An error occurred:', err);
    }
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  };

  const editDrug = (isNew, drug, showDrug) => {
    setIsNew(isNew);
    setDrug(drug);
    displayDrug(showDrug);
  };

  const resetDrug = () => {
    editDrug(true, {}, false);
    setError(undefined);
    displayDelete(false);
  };

  const deleteDrug = (drugToDelete) => {
    console.log('[Delete Drug] Initiating delete for drug:', drugToDelete);
    console.log(
      '[Delete Drug] Drug ID:',
      drugToDelete ? drugToDelete.id : undefined,
    );
    setDrug(drugToDelete);
    displayDelete(true);
  };

  const actualDelete = () => {
    console.log('[Delete Drug] Executing actual delete for drug:', drug);
    console.log(
      '[Delete Drug] Drug ID being deleted:',
      drug ? drug.id : undefined,
    );

    return webservice.drug
      .delete(drug)
      .then((result) => {
        console.log('[Delete Drug] Delete successful, result:', result);
        setBusy(true);
        resetDrug();
        getDrugs();
        return result;
      })
      .catch((deleteError) => {
        console.error('[Delete Drug] Delete failed with error:', deleteError);
        displayError(deleteError);
      });
  };

  const saveDrug = (drug) => {
    // The `org` relationship MUST be the org's FEIN (business key — Rule 17).
    // Preserve an existing record's FEIN on edit (an Administrator edit never
    // destroys it), and stamp a NEW drug with the Org Administrator's selected
    // org FEIN (activeFein -> appStore.currentOrg). New drugs for Administrators
    // are created via WAC import / approval history, which supply the FEIN —
    // never a document id, never a placeholder.
    drug.org = drug.org || appStore.currentOrg;
    return webservice.drug
      .save(drug)
      .then(() => {
        setBusy(true);
        resetDrug();
        getDrugs();
      })
      .catch((saveError) => {
        const message =
          (saveError && saveError.message) || String(saveError || '');
        // A duplicate NDC is a hard failure: the pharmacy-drug NDC is unique, so
        // route the user to edit the existing drug that owns it instead of
        // creating a second one. (graphQLWebservice already surfaced the
        // ALREADY_EXISTS message popup.)
        if (
          message.includes('ALREADY_EXISTS') ||
          /already exists/i.test(message)
        ) {
          routeToExistingDrug(drug);
          return;
        }
        displayError(saveError);
      });
  };

  // Open the live pharmacy-drug document that already owns this NDC so the user
  // edits it. When the owning drug is not in the current listing (e.g. another
  // organization's drug), the message popup already told the user what to do.
  const routeToExistingDrug = (attemptedDrug) => {
    const ndc = attemptedDrug?.ndc ? String(attemptedDrug.ndc).trim() : '';
    const existing = drugs.find(
      (d) => d && d.id && String(d.ndc || '').trim() === ndc,
    );
    setBusy(false);
    if (existing) {
      setError(undefined);
      editDrug(false, existing, true);
    } else {
      resetDrug();
    }
  };

  const getDrugs = () => {
    let orgID = security.hasAnyRole([roles.dev, roles.admin])
      ? ''
      : appStore.currentOrg
        ? appStore.currentOrg
        : 'not found';
    const companies =
      webservice.company && typeof webservice.company.all === 'function'
        ? Promise.resolve()
            .then(() => webservice.company.all())
            .then((data) => data?.orgs || [])
            .catch(() => [])
        : Promise.resolve([]);

    Promise.all([webservice.drug.all(orgID), companies])
      .then(([data, orgs]) => {
        setDrugs(addCompanyNames(data.drugs || [], orgs));
        setBusy(false);
      })
      .catch(displayError);
  };

  useEffect(() => {
    if (prevOrg !== appStore.currentOrg) {
      setBusy(true);
      getDrugs();
    }
  }, [appStore.currentOrg]);

  useEffect(() => {
    getDrugs();
  }, []);

  return (
    <div key="listings" className="home-wrapper">
      <div className="home-content">
        <h1 className="mb-zero">Drug Listings</h1>
      </div>
      <div>
        <Table
          list={sortByLastUpdatedDesc(drugs)}
          columns={columns(editDrug, deleteDrug)}
          filters={true}
          showLimit={50}
          ajaxSpinner={isBusy}
        />
      </div>
      {showDrug && (
        <Listing
          key={drug.id || 'new'}
          onClose={resetDrug}
          onSave={saveDrug}
          isNew={isNew}
          drug={drug}
          error={error}
        />
      )}
      {showDelete && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={resetDrug}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={resetDrug}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Delete"
              busy={isBusy}
              onClick={actualDelete}
              key="save-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>You are about to delete a drug.</p>
        </PopupWindow>
      )}
    </div>
  );
};

export default observer(Listings);
